<!--
PlatformWebhooksTestSendModal — "Enviar evento de teste" of the platform events
console (/config/webhooks). Registered centrally in ModalManager.vue, opened via
eventBus 'modal.platformWebhooksTestSend.open'.

Picks a subscription and a catalog event, optionally a custom JSON payload
(empty = the catalog sample), sends through app-api (test_send → events-service
delivers synchronously) and shows the outcome. Emits 'platformWebhooks.testSent'
so the page refreshes the delivery log.
-->
<template>
  <modal
    elem-id="modal_platform_webhooks_test_send"
    :title="t('Enviar evento de teste')"
    :show="isVisible"
    size="medium"
    :close-label="t('Fechar')"
    :save-label="t('Enviar agora')"
    save-icon="pi pi-send"
    :save-fn="handleSend"
    :save-disabled-fn="() => isSending || !subscriptionId || !eventName"
    save-test-id="platform-webhooks-test-send-submit"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <div class="platform-webhooks-test-send-fields">
        <select-list
          v-model="subscriptionId"
          elem-id="platformWebhooksTestTarget"
          :label="t('Serviço-alvo')"
          :itens-select="subscriptionOptions"
          :auto-select-first="true"
          data-testid="platform-webhooks-test-target"
        />
        <select-list
          v-model="eventName"
          elem-id="platformWebhooksTestEvent"
          :label="t('Evento')"
          :itens-select="eventOptions"
          :auto-select-first="true"
          data-testid="platform-webhooks-test-event"
        />
        <input-text-area
          v-model="customData"
          elem-id="platformWebhooksTestPayload"
          :label="t('Payload (opcional, JSON; vazio = exemplo do catálogo)')"
          :rows="5"
          :placeholder="selectedSample ? JSON.stringify(selectedSample) : ''"
          data-testid="platform-webhooks-test-payload"
        />
        <p v-if="selectedDescription" class="platform-webhooks-test-send-hint" data-testid="platform-webhooks-test-description">
          {{ selectedDescription }}
        </p>
      </div>

      <div v-if="result" class="platform-webhooks-test-send-result" data-testid="platform-webhooks-test-result">
        <span
          class="platform-webhooks-test-send-badge"
          :class="result.delivered ? 'is-delivered' : 'is-failed'"
          data-testid="platform-webhooks-test-result-badge"
        >
          {{ result.delivered ? t('Entregue') : t('Falhou') }}
        </span>
        <pre class="platform-webhooks-test-send-detail" data-testid="platform-webhooks-test-result-detail">{{ result.detail }}</pre>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '@/eventBus'
import { log } from '@/utils/DebugLogger'
import { usePlatformWebhooks } from '@/composables/usePlatformWebhooks'

const { t } = useI18n()
const { catalog, subscriptions, fetchCatalog, fetchSubscriptions, testSend, lastError } = usePlatformWebhooks()

const isVisible = ref(false)
const isSending = ref(false)
const subscriptionId = ref<number | ''>('')
const eventName = ref('')
const customData = ref('')
const result = ref<{ delivered: boolean; detail: string } | null>(null)

const subscriptionOptions = computed(() => subscriptions.value.map((s) => ({ ID: s.id, Name: s.delivery === 'nats' ? `${s.plugin} — nats` : `${s.plugin} — ${s.url}` })))
const eventOptions = computed(() => catalog.value.map((e) => ({ ID: e.name, Name: e.name })))
const selected = computed(() => catalog.value.find((e) => e.name === eventName.value) ?? null)
const selectedSample = computed(() => selected.value?.sample ?? null)
const selectedDescription = computed(() => selected.value?.description ?? '')

async function openModal(payload?: { subscription_id?: number; event?: string }): Promise<void> {
  log('PlatformWebhooksTestSendModal', 'openModal', 'Abrindo modal de teste', payload)
  result.value = null
  customData.value = ''
  isVisible.value = true
  await Promise.all([fetchCatalog(), fetchSubscriptions()])
  subscriptionId.value = payload?.subscription_id ?? subscriptions.value[0]?.id ?? ''
  eventName.value = payload?.event ?? catalog.value[0]?.name ?? ''
}

function closeModal(): void {
  isVisible.value = false
  result.value = null
}

async function handleSend(): Promise<void> {
  if (!subscriptionId.value || !eventName.value) return
  let data: Record<string, unknown> = {}
  if (customData.value.trim() !== '') {
    try {
      data = JSON.parse(customData.value) as Record<string, unknown>
    } catch {
      eventBus.emit('globalMessage.error', t('O payload não é um JSON válido'))
      return
    }
  }
  isSending.value = true
  result.value = null
  try {
    const r = await testSend({ subscription_id: Number(subscriptionId.value), event: eventName.value, data })
    if (!r) {
      eventBus.emit('globalMessage.error', t('Erro ao enviar') + ': ' + (lastError.value ?? ''))
      return
    }
    const code = r.delivery?.response_code ?? t('sem resposta')
    result.value = { delivered: r.delivered, detail: `${code} ${r.delivery?.response_body ?? ''}`.trim() }
    log('PlatformWebhooksTestSendModal', 'handleSend', 'Teste enviado', { delivered: r.delivered })
    eventBus.emit('platformWebhooks.testSent', { delivered: r.delivered, delivery: r.delivery })
  } finally {
    isSending.value = false
  }
}

onMounted(() => {
  eventBus.on('modal.platformWebhooksTestSend.open', openModal)
})

onBeforeUnmount(() => {
  eventBus.off('modal.platformWebhooksTestSend.open', openModal)
})
</script>

<style scoped>
.platform-webhooks-test-send-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.platform-webhooks-test-send-hint {
  margin: -0.5rem 0 0 0;
  font-size: 0.8rem;
  color: var(--p-text-muted-color, #6b7280);
}
.platform-webhooks-test-send-result {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.platform-webhooks-test-send-badge {
  align-self: flex-start;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  text-transform: uppercase;
}
.platform-webhooks-test-send-badge.is-delivered { background: #dff3ec; color: #1c7f6a; }
.platform-webhooks-test-send-badge.is-failed { background: #f8dddb; color: #b3332a; }
.platform-webhooks-test-send-detail {
  margin: 0;
  font-size: 0.75rem;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
</style>
